import { useField } from 'formik';
import React, { Ref, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Select, { StylesConfig } from 'react-select';
import AsyncSelect from 'react-select/async';
import { StateManagerProps } from 'react-select/dist/declarations/src/stateManager';
import { ActionMeta, OnChangeValue } from 'react-select/dist/declarations/src/types';
import { useTheme } from 'styled-components';

import {
  ErrorMessageStyled,
  FieldWrapperStyled,
  InputLabelStyled,
  StarStyled,
} from '../FormikInput/FormikInput.styled';
import { getSelectStyles } from './SelectDropdown.styled';

export type SelectOptionsT = {
  label: string;
  value: string | number;
};

type SelectDropdownPropsT<OptionT = SelectOptionsT> = {
  inputIconUrl?: string;
  customStyles?: StylesConfig<OptionT>;
  isAsync?: boolean;
  onChange?: (option: OptionT) => void;
  onClear?: () => void;
  isError?: boolean;
  label?: React.ReactNode;
  error?: React.ReactNode;
  isRequired?: boolean;
  name?: string;
} & Omit<StateManagerProps<OptionT>, 'onChange'>;

type FormikSelectDropdownPropsT = {
  name: string;
} & SelectDropdownPropsT;

const SelectDropdown = <OptionT,>({
  inputIconUrl,
  customStyles,
  isAsync,
  isError,
  label,
  isRequired,
  error,
  isSearchable = false,
  placeholder = null,
  onChange,
  onClear,
  ...props
}: SelectDropdownPropsT<OptionT>) => {
  const theme = useTheme();
  const selectRef = useRef<Ref<unknown>>(null);

  const [isAlignCenter, setAlignCenter] = useState(false);
  const [isOffScreenRight, setIsOffScreenRight] = useState(false);

  const styles = {
    ...getSelectStyles<OptionT>(theme, {
      inputIconUrl,
      isError,
      isAlignRight: isOffScreenRight,
      isAlignCenter,
    }),
    ...customStyles,
  };

  const onChangeValue = (
    newValue: OnChangeValue<OptionT, boolean>,
    actionMeta: ActionMeta<OptionT>
  ) => {
    if (onClear && actionMeta.action === 'clear') {
      onClear();

      return;
    }

    if (onChange) {
      onChange(newValue as OptionT);
    }
  };

  function checkIfElementOutOfScreen() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const element = (selectRef.current as any)?.menuListRef;

    if (element) {
      const rect = element.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;

      if (rect.right > viewportWidth) {
        setIsOffScreenRight(true);
      }

      if (viewportWidth - rect.width < rect.left && rect.left < rect.width) {
        setAlignCenter(true);
      }
    }
  }

  useEffect(() => {
    checkIfElementOutOfScreen();

    return () => {
      setAlignCenter(false);
      setIsOffScreenRight(false);
    };
  }, [props.menuIsOpen]);

  if (isAsync) {
    return <AsyncSelect placeholder={placeholder} styles={styles} {...props} />;
  }

  return (
    <FieldWrapperStyled>
      {label && (
        <InputLabelStyled>
          {label}
          {isRequired && <StarStyled> *</StarStyled>}
        </InputLabelStyled>
      )}

      <Select
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={selectRef as any}
        classNamePrefix="react-select"
        placeholder={placeholder}
        isSearchable={isSearchable}
        openMenuOnFocus
        styles={styles}
        onChange={onChangeValue}
        {...props}
      />

      {isError && error && <ErrorMessageStyled>{error}</ErrorMessageStyled>}
    </FieldWrapperStyled>
  );
};

export const FormikSelectDropdown: React.FC<FormikSelectDropdownPropsT> = ({
  name,
  isRequired,
  onChange,
  ...props
}) => {
  const [field, error, meta] = useField(name);
  const hasError = isRequired && error.touched && !field.value && !!error.error;

  const handleOnChange = (val: SelectOptionsT) => {
    if (!!onChange) {
      onChange(val);
    }

    meta.setValue(val.value);
  };

  return (
    <SelectDropdown
      {...field}
      isRequired={isRequired}
      isError={hasError}
      error={
        hasError ? (
          <FormattedMessage
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            id={(error.error as any)?.id}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            defaultMessage={(error.error as any)?.defaultMessage}
          />
        ) : null
      }
      value={
        props.options?.find(
          item => (item as SelectOptionsT)?.value === field.value
        ) as SelectOptionsT
      }
      onBlur={() => meta.setTouched(true)}
      onChange={handleOnChange}
      {...props}
    />
  );
};

export default SelectDropdown;
